import { get, del, getRaw, post, patch } from '../api.js';
import { decryptItem, decryptFileBytes } from '../crypto.js';
import { navigate } from '../router.js';
import { setTopbar, lucideIcons, esc } from '../app.js';
import { humanSize } from '../utils.js';
import { toast, confirmDialog } from '../dialog.js';
import { decryptItemEnvelope, decryptGroupName } from '../item-decrypt.js';

// Mirrors vault_core::policy::classification strictness ordering. Used to
// detect downgrades client-side so we can prompt before the server stamps a
// notarized event for them.
const CLASSIFICATION_LEVELS = ['public', 'standard', 'confidential', 'restricted'];
const STRICTNESS = { public: 0, standard: 1, confidential: 2, restricted: 3 };

export async function renderItemDetail(container, params) {
  const id = params.id;
  container.innerHTML = '<p class="loading">Loading...</p>';

  try {
    const [item, itemNotarizations, will, itemGroups, allGroups] = await Promise.all([
      get(`/items/${id}`),
      get('/notarizations').then(ns => ns.filter(n => n.item_id === id)).catch(() => []),
      get('/will').catch(() => null),
      get(`/items/${id}/groups`).catch(() => []),
      get('/groups').catch(() => []),
    ]);
    const inWill = will && Array.isArray(will.wrapped_items) && will.wrapped_items.some(w => w.item_id === id);
    const itemGroupIds = new Set(itemGroups.map(g => g.id));
    const groupsDisplay = itemGroups.map(g => ({ ...g, name: decryptGroupName(g) }));
    const availableGroups = allGroups.filter(g => !itemGroupIds.has(g.id)).map(g => ({ ...g, name: decryptGroupName(g) }));

    const { envelope: _env, dropBlob: _dropBlob, error: _decryptError } = decryptItemEnvelope(item);
    let envelope = _env;
    let decryptError = _decryptError;
    let plaintext = null;
    let dropBlob = _dropBlob;

    const name = envelope?.name || 'Untitled';
    const itemType = envelope?.item_type || item.item_type;
    const isFile = envelope ? !!envelope.filename : false;

    if (!isFile && !decryptError) {
      if (envelope) { plaintext = envelope.content; }
      else if (item.encrypted_blob) {
        try { plaintext = decryptItem(item.encrypted_blob, item.wrapped_key, item.nonce, item.wrapped_key_format_version, item.encrypted_blob_format_version); }
        catch (e) { decryptError = e.message; }
      }
    }

    const fileMeta = envelope
      ? { filename: envelope.filename, file_size: envelope.file_size, mime_type: envelope.mime_type }
      : {};

    const isInbox = item.source === 'inbox';
    const badgeClass = isInbox ? 'badge-drop' : isFile ? 'badge-drop' : 'badge-encrypted';
    const badgeText = isInbox ? 'Inbox' : isFile ? 'File' : 'Encrypted';
    const currentClassification = item.classification || 'standard';

    const notarizeLabel = itemNotarizations.length > 0 ? 'Re-notarize' : 'Notarize';
    setTopbar(name, `
      <button class="btn btn-secondary btn-sm" id="notarize-btn"><i data-lucide="stamp" style="width:16px;height:16px"></i> ${notarizeLabel}</button>
      <a href="#/grants/new?item_id=${esc(id)}" class="btn btn-secondary btn-sm"><i data-lucide="share-2" style="width:16px;height:16px"></i> Share</a>
      <button class="btn btn-danger btn-sm" id="delete-btn"><i data-lucide="trash-2" style="width:16px;height:16px"></i> Delete</button>
    `);

    let html = `
      <div class="breadcrumb" id="back-to-items"><i data-lucide="arrow-left" style="width:16px;height:16px"></i> Back to Items</div>
      <div class="detail-grid">
        <div class="card">
          <div class="detail-label">Name</div><div class="detail-value">${esc(name)}</div>
          <div class="detail-label">Type</div><div class="detail-value"><span class="badge ${badgeClass}">${badgeText}</span></div>
          <div class="detail-label">Legacy</div><div class="detail-value">${
            inWill
              ? '<span style="display:flex;align-items:center;gap:6px;color:var(--warning, #e6a817)"><i data-lucide="scroll-text" style="width:14px;height:14px"></i> Included in will</span>'
              : '<span style="color:var(--text-secondary)">Not in will</span>'
          }</div>
          <div class="detail-label">Created</div><div class="detail-value">${new Date(item.created_at).toLocaleString()}</div>
          <div class="detail-label">Classification</div>
          <div class="detail-value">
            <select class="form-input" id="classification-select" style="max-width:200px;padding:4px 8px;font-size:13px">
              ${CLASSIFICATION_LEVELS.map(level => `
                <option value="${level}"${currentClassification === level ? ' selected' : ''}>${level.charAt(0).toUpperCase() + level.slice(1)}</option>
              `).join('')}
            </select>
          </div>
        </div>
        <div class="card">
          <div class="detail-label">Item ID</div><div class="detail-value mono">${item.id}</div>
          <div class="detail-label">Encryption</div><div class="detail-value" style="display:flex;align-items:center;gap:6px"><i data-lucide="lock" style="width:14px;height:14px;color:var(--success)"></i> XChaCha20-Poly1305</div>
          <div class="detail-label">Key</div><div class="detail-value mono" style="color:var(--text-secondary);font-size:12px">X25519 envelope-wrapped</div>
          <div class="detail-label">Notarized</div><div class="detail-value">${
            itemNotarizations.length > 0
              ? `<a href="#/notarizations/${itemNotarizations[0].id}" style="display:flex;align-items:center;gap:6px;color:var(--success)"><i data-lucide="stamp" style="width:14px;height:14px"></i> ${new Date(itemNotarizations[0].timestamp).toLocaleString()}</a>`
              : '<span style="color:var(--text-secondary)">Not yet</span>'
          }</div>
          ${item.blob_hash ? `<div class="detail-label">Ciphertext</div><div class="detail-value" style="display:flex;align-items:center;gap:6px;color:var(--success)"><i data-lucide="shield-check" style="width:14px;height:14px"></i> Server-verified</div>` : ''}
        </div>
      </div>`;

    if (isFile) {
      html += `
        <div class="card">
          <div class="card-header"><span class="card-title">Encrypted File</span></div>
          <div style="display:flex;align-items:center;gap:16px">
            <div style="width:48px;height:48px;border-radius:10px;background:var(--bg-elevated);display:flex;align-items:center;justify-content:center">
              <i data-lucide="file" style="width:24px;height:24px;color:var(--accent)"></i>
            </div>
            <div>
              <div style="font-weight:600">${esc(fileMeta.filename)}</div>
              <div style="font-size:12px;color:var(--text-secondary);margin-top:2px">${humanSize(fileMeta.file_size)} &middot; Encrypted at rest</div>
            </div>
            <div style="margin-left:auto">
              <button class="btn btn-secondary btn-sm" id="download-btn"><i data-lucide="download" style="width:14px;height:14px"></i> Download</button>
            </div>
          </div>
          <div id="file-preview"></div>
        </div>`;
    } else {
      html += `
        <div class="card">
          <div class="card-header"><span class="card-title">Content</span></div>
          ${decryptError
            ? `<div class="msg msg-error">${esc(decryptError)}</div>`
            : `<div class="code-block">${esc(plaintext)}</div>`
          }
        </div>`;
    }

    // Collections card
    html += `
      <div class="card">
        <div class="card-header"><span class="card-title">Collections</span></div>
        <div id="collections-chips" style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:${availableGroups.length ? '12px' : '0'}">
          ${groupsDisplay.length ? groupsDisplay.map(g => `
            <span class="group-badge group-badge-removable" data-group-id="${g.id}">
              ${esc(g.name)}
              <button class="remove-group-btn" data-group-id="${g.id}" title="Remove from collection">&times;</button>
            </span>
          `).join('') : '<span style="color:var(--text-secondary);font-size:13px">Not in any collection</span>'}
        </div>
        ${availableGroups.length ? `
          <div style="display:flex;gap:8px;align-items:center">
            <select class="form-input" id="add-to-group-select" style="flex:1">
              <option value="">Add to collection...</option>
              ${availableGroups.map(g => `<option value="${g.id}">${esc(g.name)}</option>`).join('')}
            </select>
            <button class="btn btn-primary btn-sm" id="add-to-group-btn">Add</button>
          </div>
        ` : ''}
      </div>`;

    container.innerHTML = html;
    lucideIcons();

    // Collections: remove from group
    container.querySelectorAll('.remove-group-btn').forEach(btn => {
      btn.onclick = async (e) => {
        e.stopPropagation();
        const groupId = btn.dataset.groupId;
        try {
          await del(`/groups/${groupId}/items/${id}`);
          renderItemDetail(container, params);
        } catch (err) { toast(err.message); }
      };
    });

    // Collections: add to group
    const addToGroupBtn = document.getElementById('add-to-group-btn');
    if (addToGroupBtn) {
      addToGroupBtn.onclick = async () => {
        const select = document.getElementById('add-to-group-select');
        const groupId = select.value;
        if (!groupId) return;
        try {
          await post(`/groups/${groupId}/items`, { item_id: id });
          renderItemDetail(container, params);
        } catch (err) { toast(err.message); }
      };
    }

    const classificationSelect = document.getElementById('classification-select');
    classificationSelect.onchange = async (e) => {
      const newCls = e.target.value;
      if (newCls === currentClassification) return;

      // Disable before any await so a second change during the confirm dialog
      // or the in-flight PATCH can't shadow this one.
      classificationSelect.disabled = true;

      // Put the select back the way the user found it: reverted to the stored
      // value, re-enabled, optionally with a toast explaining what went wrong.
      const revert = (errMsg) => {
        classificationSelect.value = currentClassification;
        classificationSelect.disabled = false;
        if (errMsg) toast(errMsg);
      };

      const isDowngrade = STRICTNESS[newCls] < STRICTNESS[currentClassification];
      if (isDowngrade) {
        const ok = await confirmDialog(
          `Downgrade classification from ${currentClassification} to ${newCls}? This will be recorded as a notarized event.`,
          { danger: true }
        );
        if (!ok) { revert(); return; }
      }

      const sendPatch = (ack) => patch(`/items/${id}/classification`, {
        classification: newCls,
        acknowledge_grant_breakage: ack,
      });
      const onSuccess = () => {
        toast(`Classification updated to ${newCls}`, 'success');
        // Re-render so the Notarized row (new downgrade event) and any other
        // derived fields pick up the server's fresh state.
        renderItemDetail(container, params);
      };

      try {
        await sendPatch(false);
        onSuccess();
      } catch (err) {
        // Only the grant-breakage 422 has an acknowledge-and-retry path — other
        // 422s (if any future route variant adds them) should revert like any
        // other error. Sniff the message shape the server emits in
        // vault-api/src/routes/items.rs::patch_item_classification.
        const isGrantBreakage =
          err.status === 422 && /^reclassify would break/.test(err.message);
        if (!isGrantBreakage) { revert(err.message); return; }

        // Strip the dev-y tail the server appends for CLI readers — the
        // confirm button here is the acknowledge action, so the instruction
        // is redundant. Kept loose (optional trailing period) in case the
        // server ever adds one.
        const cleanMsg = err.message.replace(
          / Set acknowledge_grant_breakage=true to proceed\.?$/, ''
        );
        const ack = await confirmDialog(
          `${cleanMsg}. Acknowledge and proceed anyway?`,
          { danger: true }
        );
        if (!ack) { revert(); return; }

        try {
          await sendPatch(true);
          onSuccess();
        } catch (err2) {
          revert(err2.message);
        }
      }
    };

    document.getElementById('back-to-items').onclick = () => navigate('#/items');
    document.getElementById('delete-btn').onclick = async () => {
      if (!await confirmDialog('Delete this item?', { danger: true })) return;
      try { await del(`/items/${id}`); navigate('#/items'); }
      catch (err) { toast(err.message); }
    };

    document.getElementById('notarize-btn').onclick = async () => {
      try {
        const btn = document.getElementById('notarize-btn');
        btn.disabled = true;
        btn.innerHTML = '<i data-lucide="loader" style="width:16px;height:16px"></i> Notarizing...';
        lucideIcons();

        // Hash the decrypted content client-side using Web Crypto API
        let dataToHash;
        if (plaintext) {
          dataToHash = new TextEncoder().encode(plaintext);
        } else if (isFile && envelope) {
          // For files we need to fetch and decrypt first
          const encBuf = await getRaw(`/items/${id}/blob`);
          const fileWK = envelope.file_wrapped_key || item.wrapped_key;
          const fileNonce = envelope.file_nonce || item.nonce;
          dataToHash = decryptFileBytes(new Uint8Array(encBuf), fileWK, fileNonce);
        } else {
          throw new Error('Cannot hash: no decrypted content available');
        }

        const hashBuf = await crypto.subtle.digest('SHA-256', dataToHash);
        const contentHash = Array.from(new Uint8Array(hashBuf)).map(b => b.toString(16).padStart(2, '0')).join('');
        const result = await post('/notarizations', { content_hash: contentHash, item_id: id });
        toast('Notarized successfully', 'success');
        navigate(`#/notarizations/${result.id}`);
      } catch (err) {
        toast(err.message);
        const btn = document.getElementById('notarize-btn');
        if (btn) {
          btn.disabled = false;
          btn.innerHTML = '<i data-lucide="stamp" style="width:16px;height:16px"></i> Notarize';
          lucideIcons();
        }
      }
    };

    if (isFile) {
      const downloadBtn = document.getElementById('download-btn');
      const preview = document.getElementById('file-preview');
      let decryptedUrl = null;
      let decryptedBytes = dropBlob || null; // already decrypted for claimed drops

      if (decryptedBytes) {
        const mime = fileMeta.mime_type || 'application/octet-stream';
        decryptedUrl = URL.createObjectURL(new Blob([decryptedBytes], { type: mime }));
      }

      async function fetchAndDecrypt() {
        if (decryptedBytes) return;
        downloadBtn.disabled = true;
        downloadBtn.innerHTML = '<i data-lucide="download" style="width:14px;height:14px"></i> Downloading...';
        lucideIcons();
        const encryptedBuf = await getRaw(`/items/${id}/blob`);
        const fileWK = envelope?.file_wrapped_key || item.wrapped_key;
        const fileNonce = envelope?.file_nonce || item.nonce;
        decryptedBytes = decryptFileBytes(new Uint8Array(encryptedBuf), fileWK, fileNonce);
        const mime = fileMeta.mime_type || 'application/octet-stream';
        decryptedUrl = URL.createObjectURL(new Blob([decryptedBytes], { type: mime }));
        downloadBtn.disabled = false;
        downloadBtn.innerHTML = '<i data-lucide="download" style="width:14px;height:14px"></i> Download';
        lucideIcons();
      }

      function triggerDownload() {
        const a = document.createElement('a');
        a.href = decryptedUrl;
        a.download = fileMeta.filename;
        a.click();
      }

      downloadBtn.onclick = async () => {
        try {
          await fetchAndDecrypt();
          triggerDownload();
          showPreview();
        } catch (err) {
          preview.innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
          downloadBtn.disabled = false;
          downloadBtn.innerHTML = '<i data-lucide="download" style="width:14px;height:14px"></i> Download';
          lucideIcons();
        }
      };

      function showPreview() {
        const mime = fileMeta.mime_type || '';
        if (mime.startsWith('image/')) {
          preview.innerHTML = `<img src="${decryptedUrl}" style="max-width:100%;margin-top:1rem;border-radius:8px" alt="${esc(fileMeta.filename)}">`;
        } else if (mime.startsWith('text/')) {
          const text = new TextDecoder().decode(decryptedBytes);
          preview.innerHTML = `<div class="code-block" style="margin-top:1rem">${esc(text)}</div>`;
        }
      }

      if (fileMeta.file_size < 2 * 1024 * 1024) {
        fetchAndDecrypt().then(showPreview).catch((err) => {
          downloadBtn.disabled = false;
          downloadBtn.innerHTML = '<i data-lucide="download" style="width:14px;height:14px"></i> Download';
          lucideIcons();
          preview.innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
        });
      }
    }
  } catch (err) {
    container.innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
  }
}
